import "./Projects.css";

import { OrganisationTypeConstants } from "../../../constants";
import {
  AssetManagerSingleCardWithTabsTemplate,
  DeveloperSingleCardWithTabsTemplate,
  OverdueTaskPanel,
} from "../../../templates";
import { UpcomingMilestonesTaskPanel } from "../../../templates/UpcomingMilestonesTaskPanel";
import { WorkflowTaskPanel } from "../../../templates/WorkflowTaskPanel";
import { Button, Column, KanaTooltip, OverdueTasks, Row, UpcomingMilestones } from "../../../widget";
import { DashboardPieCard } from "../components";
import { useProjects } from "./useProjects";

const Projects = (): JSX.Element => {
  const {
    tabsList,
    kpiCorners,
    canCreateProject,
    onNewProjectClick,
    phaseChartData,
    isLoading,
    currentUserType,
    upcomingMilestones,
    overdueTasks,
    isOverdueTasksPanelOpen,
    setIsOverdueTasksPanelOpen,
    isLoadingMilestones,
    hasMilestonesError,
    isUpcomingMilestonesPanelOpen,
    setIsUpcomingMilestonesPanelOpen,
    workflow,
    selectedPanelTask,
    showWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    handleRefresh,
    refreshTasks,
    setSelectedPanelTask,
    setShowWorkflowTaskPanel,
  } = useProjects();

  const kpis = (): JSX.Element | undefined => {
    return isLoading ? (
      <Row spacingV="ml">
        <Column span={6}>
          <div className="MilestonesLoadingCard">
            <p className="body01">Upcoming milestones</p>
            <p className="body2 NoData">Loading...</p>
          </div>
        </Column>
        <Column span={4}>
          <DashboardPieCard
            title="Project phase"
            data={{}}
            tooltip={
              <KanaTooltip
                tooltipHeader="Project Phase:"
                tooltipText="Shows the number of projects by phases of their workflow."
              />
            }
            isGradient
            colours="roadmap"
            corners={kpiCorners}
          />
        </Column>
        <Column span={2}>
          <div className={`TasksLoadingCard TasksLoadingCard_${kpiCorners}`}>
            <p className="body01">Tasks</p>
            <p className="body2 NoData">Loading...</p>
          </div>
        </Column>
      </Row>
    ) : (
      <div className="ProjectsPageKpiContainer">
        <Row>
          <Column span={6}>
            <UpcomingMilestones
              milestones={upcomingMilestones}
              onViewAll={() => setIsUpcomingMilestonesPanelOpen(true)}
              isLoading={isLoadingMilestones}
              hasError={hasMilestonesError}
              loadingCardCount={3}
            />
          </Column>
          <Column span={4}>
            <DashboardPieCard
              title="Project phase"
              data={phaseChartData}
              tooltip={
                <KanaTooltip
                  tooltipHeader="Project phases:"
                  tooltipText="Shows the number of projects by phases of their workflow."
                />
              }
              isGradient
              colours="roadmap"
              corners={kpiCorners}
            />
          </Column>
          <Column span={2}>
            <OverdueTasks
              amount={overdueTasks.amount}
              label={overdueTasks.label}
              onViewAll={() => setIsOverdueTasksPanelOpen(true)}
              corners={kpiCorners}
            />
          </Column>
        </Row>
        <div>
          <OverdueTaskPanel
            show={isOverdueTasksPanelOpen}
            onClose={() => setIsOverdueTasksPanelOpen(false)}
            setSelectedPanelTask={setSelectedPanelTask}
            setShowWorkflowTaskPanel={setShowWorkflowTaskPanel}
            refreshTasks={refreshTasks}
          />
          <UpcomingMilestonesTaskPanel
            show={isUpcomingMilestonesPanelOpen}
            onClose={() => setIsUpcomingMilestonesPanelOpen(false)}
            setSelectedPanelTask={setSelectedPanelTask}
            setShowWorkflowTaskPanel={setShowWorkflowTaskPanel}
            refreshTasks={refreshTasks}
          />
          <WorkflowTaskPanel
            task={selectedPanelTask}
            currentStepUuid={selectedPanelTask?.step?.uuid}
            workflow={workflow}
            show={showWorkflowTaskPanel}
            refresh={refreshTasks}
            handleRefresh={handleRefresh}
            onClose={handleCloseWorkflowTaskPanel}
          />
        </div>
      </div>
    );
  };

  const quickActions = canCreateProject ? (
    <div className="ProjectsQuickActions">
      <Button text="Create new project" onClick={onNewProjectClick} variant="tertiary" />
    </div>
  ) : undefined;

  if (currentUserType === OrganisationTypeConstants.ASSET_MANAGER) {
    return AssetManagerSingleCardWithTabsTemplate({
      activeElement: "Projects",
      pageTitle: "Projects",
      tabsList,
      carousel: kpis(),
      quickActions,
    });
  }

  return DeveloperSingleCardWithTabsTemplate({
    activeElement: "Projects",
    pageTitle: "Projects",
    tabsList,
    titlePill: "",
    developer: undefined,
    developerIcon: undefined,
    carousel: kpis(),
    backButtonText: undefined,
    handleBackClick: undefined,
    quickActions,
    currentUserType,
  });
};

export default Projects;
